export const HIDDEN_ROOT_ORGS = new Set(['未分配部门', '—']);

export function normalizeOrgLabel(c: string | null | undefined): string {
  return (c || '').trim() || '—';
}

export function isHiddenRootOrg(company: string | null | undefined): boolean {
  return HIDDEN_ROOT_ORGS.has(normalizeOrgLabel(company));
}

export function countVisibleDirectoryItems(items: DirectoryItem[] | undefined): number {
  return (items ?? []).filter((i) => !isHiddenRootOrg(i.company)).length;
}

/** 部门筛选标签：有二级部门时为「dept1 / dept2」，否则为 dept1 */
export function formatDeptFilterLabel(dept1: string, dept2?: string): string {
  const d1 = (dept1 || '').trim();
  const d2 = (dept2 || '').trim();
  if (!d1 || d1 === '—') return '';
  if (d2 && d2 !== '—') return `${d1} / ${d2}`;
  return d1;
}

export function itemDeptFilterLabel(item: Pick<DirectoryItem, 'dept1' | 'dept2'>): string {
  return formatDeptFilterLabel(item.dept1, item.dept2);
}

export type DirectoryItem = {
  empId: number;
  name: string;
  empNo: string;
  company: string;
  dept1: string;
  dept2: string;
  dept: string;
  deptPathDb: string | null;
  position: string;
  rosterHit: boolean;
};

export type DirectoryResp = {
  orgSource?: 'dingtalk_dept_tree';
  departmentCount?: number;
  items: DirectoryItem[];
};

export type Dept1Group = {
  dept1: string;
  total: number;
  /** 二级部门 -> 成员；'' 代表无二级部门，直接挂在一级下 */
  dept2Map: Map<string, DirectoryItem[]>;
};

export type OrgPanel = {
  rootOrg: string;
  grouped: Map<string, Dept1Group>;
  totalInOrg: number;
};

/** 按公司(rootOrg) → 一级部门 → 二级部门 分组；跳过空或 '—' 的一级部门 */
export function groupByOrgThenDept(items: DirectoryItem[]): OrgPanel[] {
  const byOrg = new Map<string, DirectoryItem[]>();
  for (const it of items) {
    const org = normalizeOrgLabel(it.company);
    if (!byOrg.has(org)) byOrg.set(org, []);
    byOrg.get(org)!.push(it);
  }
  const keys = Array.from(byOrg.keys()).sort((a, b) => a.localeCompare(b, 'zh-CN'));
  return keys.map((rootOrg) => {
    const bucket = byOrg.get(rootOrg)!;
    return {
      rootOrg,
      grouped: groupByDept1Dept2(bucket),
      totalInOrg: bucket.length,
    };
  });
}

/** 门店内按 1级 / 2级部门分组。空一级部门、'—' 一级部门会被过滤掉。 */
export function groupByDept1Dept2(items: DirectoryItem[]): Map<string, Dept1Group> {
  const out = new Map<string, Dept1Group>();
  for (const it of items) {
    const d1 = (it.dept1 || '').trim();
    if (!d1 || d1 === '—') continue;
    const d2 = (it.dept2 || '').trim();
    if (!out.has(d1)) {
      out.set(d1, { dept1: d1, total: 0, dept2Map: new Map() });
    }
    const g = out.get(d1)!;
    g.total += 1;
    if (!g.dept2Map.has(d2)) g.dept2Map.set(d2, []);
    g.dept2Map.get(d2)!.push(it);
  }
  return out;
}

/** 一级部门下是否存在非空二级部门 */
export function dept1HasDept2Children(group: Dept1Group): boolean {
  for (const d2 of group.dept2Map.keys()) {
    if (d2.trim()) return true;
  }
  return false;
}

/** 一级部门排序：有二级部门的在前，无二级部门的（如保洁组）沉底；同组内按名称 */
export function sortedDept1Groups(grouped: Map<string, Dept1Group>): Dept1Group[] {
  return Array.from(grouped.values()).sort((a, b) => {
    const aHas = dept1HasDept2Children(a);
    const bHas = dept1HasDept2Children(b);
    if (aHas !== bHas) return aHas ? -1 : 1;
    return a.dept1.localeCompare(b.dept1, 'zh-CN');
  });
}

/** undefined = 不限三级；此值 = 仅无 2 级部门 */
export const DEPT2_EMPTY = '__EMPTY__';

export type OverviewKpi = {
  evalTasks: number;
  evalDone: number;
  hpCount: number;
  suspiciousCount: number;
  ninebox: Record<string, number>;
  competencyTasks?: number;
};
